// ==========================================================
// Craftsman Color Mixing Lab Modal Controller
// ==========================================================

import { ColorMixer, type PigmentRecipe, type RGB } from '../engine/ColorMixer';
import { sounds } from '../audio/SoundEffects';
import { i18n } from '../i18n/I18nService';

export class ColorLabModalController {
  private modalEl: HTMLElement;
  private targetSwatchEl: HTMLElement;
  private targetCodeEl: HTMLElement;
  private currentSwatchEl: HTMLElement;
  private currentCodeEl: HTMLElement;
  private matchPercentEl: HTMLElement;
  private matchRingEl: SVGCircleElement;
  private matchRatingEl: HTMLElement;
  private dishCanvas: HTMLCanvasElement;
  private dishCtx: CanvasRenderingContext2D;

  private targetHex: string = '#E74C3C';
  private currentRgb: RGB = { r: 255, g: 255, b: 255 };

  private recipe: PigmentRecipe = {
    cyan: 0,
    magenta: 0,
    yellow: 0,
    black: 0,
    white: 0
  };

  private dropletVisuals: Array<{ x: number; y: number; color: string; radius: number }> = [];

  private onApplyColorCallback?: (hex: string, matchScore: number) => void;

  constructor() {
    this.modalEl = document.getElementById('modal-color-lab')!;
    this.targetSwatchEl = document.getElementById('lab-target-swatch')!;
    this.targetCodeEl = document.getElementById('lab-target-code')!;
    this.currentSwatchEl = document.getElementById('lab-current-swatch')!;
    this.currentCodeEl = document.getElementById('lab-current-code')!;
    this.matchPercentEl = document.getElementById('lab-match-percent')!;
    this.matchRingEl = document.getElementById('gauge-match-ring') as unknown as SVGCircleElement;
    this.matchRatingEl = document.getElementById('lab-match-rating')!;
    this.dishCanvas = document.getElementById('mixing-dish-canvas') as HTMLCanvasElement;
    this.dishCtx = this.dishCanvas.getContext('2d')!;

    this.initEvents();
    this.updateLanguageUI();

    i18n.subscribe(() => {
      this.updateLanguageUI();
      this.recalculate();
    });
  }

  private initEvents() {
    // 1. Close / Cancel
    document.getElementById('btn-close-lab')?.addEventListener('click', () => {
      sounds.playClick();
      this.close();
    });
    document.getElementById('btn-cancel-lab')?.addEventListener('click', () => {
      sounds.playClick();
      this.close();
    });

    // 2. Pigment Droppers
    const pigments: Array<keyof PigmentRecipe> = ['cyan', 'magenta', 'yellow', 'black', 'white'];
    pigments.forEach(p => {
      const dropper = document.querySelector(`.pigment-dropper[data-color="${p}"]`);
      if (!dropper) return;

      const btnPlus = dropper.querySelector('.btn-plus');
      const btnMinus = dropper.querySelector('.btn-minus');
      const countEl = dropper.querySelector('.drop-count');

      btnPlus?.addEventListener('click', () => {
        sounds.playDrop();
        this.recipe[p] = Math.min(20, this.recipe[p] + 1);
        if (countEl) countEl.textContent = String(this.recipe[p]);
        this.addDropletVisual(p);
        this.recalculate();
      });

      btnMinus?.addEventListener('click', () => {
        sounds.playClick();
        this.recipe[p] = Math.max(0, this.recipe[p] - 1);
        if (countEl) countEl.textContent = String(this.recipe[p]);
        this.recalculate();
      });
    });

    // 3. Stir / Mix Button
    document.getElementById('btn-mix-stir')?.addEventListener('click', () => {
      sounds.playStir();
      this.animateStir();
    });

    // 4. Reset Dish
    document.getElementById('btn-reset-dish')?.addEventListener('click', () => {
      sounds.playClick();
      this.resetDish();
    });

    // 5. Apply Color
    document.getElementById('btn-apply-lab-color')?.addEventListener('click', () => {
      sounds.playFanfare(3);
      const currentHex = ColorMixer.rgbToHex(this.currentRgb);
      const matchScore = ColorMixer.calculateMatchPercentage(this.targetHex, this.currentRgb).percentage;

      if (this.onApplyColorCallback) {
        this.onApplyColorCallback(currentHex, matchScore);
      }
      this.close();
    });
  }

  public open(targetHex: string, onApply: (hex: string, matchScore: number) => void) {
    this.targetHex = targetHex;
    this.onApplyColorCallback = onApply;

    this.targetSwatchEl.style.backgroundColor = targetHex;
    this.targetCodeEl.textContent = targetHex.toUpperCase();

    this.resetDish();
    this.modalEl.classList.remove('hidden');
  }

  public close() {
    this.modalEl.classList.add('hidden');
  }

  private resetDish() {
    this.recipe = { cyan: 0, magenta: 0, yellow: 0, black: 0, white: 0 };
    document.querySelectorAll('.drop-count').forEach(el => {
      el.textContent = '0';
    });
    this.dropletVisuals = [];
    this.currentRgb = { r: 255, g: 255, b: 255 };
    this.renderDish();
    this.recalculate();
  }

  private addDropletVisual(pigment: keyof PigmentRecipe) {
    const colorMap: Record<keyof PigmentRecipe, string> = {
      cyan: '#00BCD4',
      magenta: '#E91E63',
      yellow: '#FFEB3B',
      black: '#212121',
      white: '#FAFAFA'
    };

    const pad = 30;
    const x = pad + Math.random() * (this.dishCanvas.width - pad * 2);
    const y = pad + Math.random() * (this.dishCanvas.height - pad * 2);
    const radius = 8 + Math.random() * 8;

    this.dropletVisuals.push({ x, y, color: colorMap[pigment], radius });
    this.renderDish();
  }

  private renderDish() {
    const w = this.dishCanvas.width;
    const h = this.dishCanvas.height;

    this.dishCtx.clearRect(0, 0, w, h);

    // Porcelain dish base
    this.dishCtx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    this.dishCtx.beginPath();
    this.dishCtx.roundRect(4, 4, w - 8, h - 8, 12);
    this.dishCtx.fill();

    // Dish border
    this.dishCtx.strokeStyle = 'rgba(200, 200, 200, 0.6)';
    this.dishCtx.lineWidth = 2;
    this.dishCtx.stroke();

    // Draw Droplets
    this.dropletVisuals.forEach(drop => {
      this.dishCtx.save();
      this.dishCtx.fillStyle = drop.color;
      this.dishCtx.beginPath();
      this.dishCtx.arc(drop.x, drop.y, drop.radius, 0, Math.PI * 2);
      this.dishCtx.fill();
      this.dishCtx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
      this.dishCtx.lineWidth = 1;
      this.dishCtx.stroke();
      this.dishCtx.restore();
    });
  }

  private animateStir() {
    let frame = 0;
    const totalFrames = 30;
    const finalHex = ColorMixer.rgbToHex(this.currentRgb);

    const anim = () => {
      frame++;
      const progress = frame / totalFrames;

      this.dishCtx.fillStyle = `rgba(${this.currentRgb.r}, ${this.currentRgb.g}, ${this.currentRgb.b}, ${progress * 0.9})`;
      this.dishCtx.beginPath();
      this.dishCtx.roundRect(4, 4, this.dishCanvas.width - 8, this.dishCanvas.height - 8, 12);
      this.dishCtx.fill();

      if (frame < totalFrames) {
        requestAnimationFrame(anim);
      } else {
        this.dropletVisuals = [];
        this.renderDish();
        this.dishCtx.fillStyle = finalHex;
        this.dishCtx.beginPath();
        this.dishCtx.roundRect(4, 4, this.dishCanvas.width - 8, this.dishCanvas.height - 8, 12);
        this.dishCtx.fill();
      }
    };
    anim();
  }

  private recalculate() {
    this.currentRgb = ColorMixer.mixPigments(this.recipe);
    const currentHex = ColorMixer.rgbToHex(this.currentRgb);

    this.currentSwatchEl.style.backgroundColor = currentHex;
    this.currentCodeEl.textContent = currentHex.toUpperCase();

    const totalDrops = Object.values(this.recipe).reduce((a, b) => a + b, 0);
    const t = i18n.dict.colorLab;

    if (totalDrops === 0) {
      this.matchPercentEl.textContent = '0%';
      this.matchRingEl.style.strokeDashoffset = '264';
      this.matchRatingEl.textContent = t.mixingStatus;
      this.matchRatingEl.className = 'match-rating-badge';
      return;
    }

    const matchScore = ColorMixer.calculateMatchPercentage(this.targetHex, this.currentRgb).percentage;
    this.matchPercentEl.textContent = `${matchScore}%`;

    // Gauge Offset (Circumference ~ 264)
    const offset = 264 - (264 * matchScore) / 100;
    this.matchRingEl.style.strokeDashoffset = String(offset);

    if (matchScore >= 98) {
      this.matchRatingEl.textContent = t.ratings.perfect;
      this.matchRatingEl.className = 'match-rating-badge rating-god';
    } else if (matchScore >= 90) {
      this.matchRatingEl.textContent = t.ratings.great;
      this.matchRatingEl.className = 'match-rating-badge rating-master';
    } else if (matchScore >= 80) {
      this.matchRatingEl.textContent = t.ratings.good;
      this.matchRatingEl.className = 'match-rating-badge rating-good';
    } else {
      this.matchRatingEl.textContent = t.ratings.mixing;
      this.matchRatingEl.className = 'match-rating-badge';
    }
  }

  public updateLanguageUI() {
    const t = i18n.dict.colorLab;
    const titleEl = document.querySelector('.color-lab-card h3');
    if (titleEl) titleEl.textContent = t.title;

    const subEl = document.querySelector('.color-lab-card .modal-sub');
    if (subEl) subEl.textContent = t.subtitle;

    const boxLabels = document.querySelectorAll('.color-lab-card .box-label');
    if (boxLabels[0]) boxLabels[0].textContent = t.targetLabel;
    if (boxLabels[1]) boxLabels[1].textContent = t.currentLabel;

    const gaugeSub = document.querySelector('.gauge-sub');
    if (gaugeSub) gaugeSub.textContent = t.matchLabel;

    const tubeNames: Record<string, string> = {
      cyan: t.cyan,
      magenta: t.magenta,
      yellow: t.yellow,
      black: t.black,
      white: t.white
    };

    document.querySelectorAll('.pigment-dropper').forEach(dropper => {
      const color = dropper.getAttribute('data-color');
      if (color && tubeNames[color]) {
        const nameEl = dropper.querySelector('.tube-name');
        if (nameEl) nameEl.textContent = tubeNames[color];
      }
    });

    const stirBtn = document.getElementById('btn-mix-stir');
    if (stirBtn) stirBtn.textContent = t.stirBtn;

    const resetDishBtn = document.getElementById('btn-reset-dish');
    if (resetDishBtn) resetDishBtn.textContent = t.resetBtn;

    const cancelLabBtn = document.getElementById('btn-cancel-lab');
    if (cancelLabBtn) cancelLabBtn.textContent = t.closeBtn;

    const applyLabBtn = document.querySelector('#btn-apply-lab-color span');
    if (applyLabBtn) applyLabBtn.textContent = t.applyBtn;
  }
}
